<template>
  <div class="flex flex-col gap-y-4">
    <div class="flex gap-x-4">
      <span>{{ $t('selectIdentifier') }}</span>
      <div class="flex gap-x-6">
        <Field :value="1" name="isByUnifiedNumber" type="radio" v-slot="{ field }">
          <label class="flex gap-x-2">
            <input class="accent-primary" type="radio" v-bind="field" />
            {{ $t('unifiedNumber') }}
          </label>
        </Field>
        <Field :value="0" name="isByUnifiedNumber" type="radio" v-slot="{ field }">
          <label class="flex gap-x-2">
            <input class="accent-primary" type="radio" v-bind="field" />
            {{ $t('emiratesIdentityNumber') }}
          </label>
        </Field>
      </div>
    </div>

    <div class="flex flex-col gap-y-0.5">
      <Field
        v-if="isByUnifiedNumber === 1"
        :rules="isRequired"
        name="person.unifiedNumber"
        v-slot="{ field, errors: errs }"
      >
        <SimpleTextInput
          :field="field"
          :errors="errs"
          :label="$t('unifiedNumber')"
          direction="flex-col"
          @on-blur="handleBlur"
        />
      </Field>
      <Field
        v-else
        :rules="isRequired"
        name="person.identityNumber"
        v-slot="{ field, errors: errs }"
      >
        <SimpleTextInput
          :field="field"
          :errors="errs"
          :label="$t('emiratesIdentityNumber')"
          mask="###-####-#######-#"
          direction="flex-col"
          @on-blur="handleBlur"
        />
      </Field>
      <div class="flex justify-end">
        <button
          class="rounded-md bg-primary/80 hover:bg-primary flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
          @click.prevent="handleBlur"
        >
          <div class="flex justify-center items-center h-full bg-black/10 p-2 rounded-s">
            <faMagnifyingGlass class="w-3 fill-white" />
          </div>
          <span class="px-1.5 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
            {{ $t('search') }}
          </span>
        </button>
      </div>
    </div>

    <div v-if="registeredMobileNumbers" class="flex flex-col gap-y-1">
      <InfoBar variant="warning">
        <span class="pt-1 flex-1 text-white text-justify text-base">
          {{ $t('updateYourRigisteredMobileNumberFromHere') }}
          <RouterLink to="/" class="font-bolder underline underline-offset-2">
            {{ $t('here') }}
          </RouterLink>
        </span>
      </InfoBar>
      <h4>{{ $t('SelectMobileNumber') }}</h4>
      <Field
        :key="key"
        :value="mobileNumber"
        v-for="(mobileNumber, key) in registeredMobileNumbers"
        name="selectedMobileNumber"
        type="radio"
        v-slot="{ field }"
      >
        <label class="flex gap-x-2">
          <input class="accent-primary" type="radio" v-bind="field" />
          <span>{{ mask.masked(mobileNumber) }}</span>
        </label>
      </Field>
      <div class="flex justify-end gap-x-2">
        <button
          :disabled="!selectedMobileNumber || isOtpSent"
          class="rounded-md bg-primary/80 hover:bg-primary flex items-center hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed"
          @click.prevent="sendOtp"
        >
          <div class="flex justify-center items-center h-full bg-black/10 p-2 rounded-s">
            <faPaperPlane class="w-3 fill-white" />
          </div>
          <span class="px-1.5 ltr:font-sans tracking-wide text-white text-sm pb-[1px]">
            {{ $t('sendOTP') }}
          </span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import faPaperPlane from '@/assets/icons/faPaperPlane.svg?component'
import faMagnifyingGlass from '@/assets/icons/faMagnifyingGlass.svg?component'
import InfoBar from '@/components/Form/InfoBar.vue'
import SimpleTextInput from '@/components/SimpleForm/TextInput.vue'
import axios from '@/services'
import { Mask } from 'maska'
import { notify } from 'notiwind'
import { Field, useFieldValue, useFormValues } from 'vee-validate'
import { ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

defineProps({
  values: Object
})

const mask = new Mask({ mask: '###!*!*!*!*!*####' })

const i18n = useI18n()
const formValues = useFormValues()
const isByUnifiedNumber = useFieldValue('isByUnifiedNumber')
const selectedMobileNumber = useFieldValue('selectedMobileNumber')
const registeredMobileNumbers = ref()
const isOtpSent = ref(false)

watch(
  () => [formValues.value.verifyByMobileNumber, formValues.value.isByUnifiedNumber],
  () => {
    resetMobileForm()
  }
)

function isRequired(value) {
  if (value && value.trim()) {
    return true
  }

  return 'INVALID_REQUIRED'
}

async function handleBlur() {
  const {
    person: { identityNumber, unifiedNumber }
  } = formValues.value

  if (!identityNumber && !unifiedNumber) return
  const url = `/api/User/getRegisteredMobileNumbers/unifiedNumber/${unifiedNumber}/identityNumber/${identityNumber}`
  const data = await axios.get(url)

  if ('registeredMobileNumbers' in data === false) {
    notify(
      {
        group: 'error',
        title: i18n.t('errorSetDefaultAddress'),
        text: i18n.t('invalidData')
      },
      6000
    )
  }

  if (!data?.registeredMobileNumbers) {
    notify(
      {
        group: 'error',
        title: i18n.t('NoMobleNumbersExistMsgTitle'),
        text: i18n.t('NoMobleNumbersExistMsg')
      },
      6000
    )
  }

  // registeredMobileNumbers.value = data.registeredMobileNumbers
  registeredMobileNumbers.value = ['971524132522', '971564390566', '971567981919', '971541234566']
}

async function sendOtp() {
  const {
    person: { identityNumber, unifiedNumber }
  } = formValues.value

  const slugs = [
    '/api/user/sendOtp/unifiedNumber',
    unifiedNumber || 'null',
    'identityNumber',
    identityNumber || 'null',
    'mobileNumber',
    selectedMobileNumber.value
  ]
  try {
    const data = await axios.get(slugs.join('/'))
  } catch (error) {}

  isOtpSent.value = true
}

function resetMobileForm() {
  registeredMobileNumbers.value = null
  isOtpSent.value = false
}
</script>

<style lang="scss" scoped></style>
